import { useRef, useEffect } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Card from './Card'
import { sanitizeRichText } from '../services/xssProtection'
import { validateSafeURL } from '../services/xssProtection'
import { stripHTML } from '../utils/htmlSanitization'

const RichTextEditor = ({ value, onChange, placeholder = 'Enter notes...', disabled = false }) => {
  const quillRef = useRef(null)

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link'],
      [{ color: [] }, { background: [] }],
      ['clean'],
    ],
    // Custom link handler to validate URLs
    clipboard: {
      matchVisual: false,
    },
  }

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent',
    'link',
    'color',
    'background',
  ]

  // Convert Quill Delta to plain text for search
  const getPlainText = (delta) => {
    if (!delta || !delta.ops) return ''
    return delta.ops
      .map((op) => (typeof op.insert === 'string' ? op.insert : ''))
      .join('')
  }

  // Sanitize content before passing to onChange
  const handleChange = (content, delta, source, editor) => {
    // Sanitize the HTML content
    const sanitizedContent = sanitizeRichText(content)
    
    // Get delta and sanitize links in delta
    const deltaContent = editor.getContents()
    if (deltaContent && deltaContent.ops) {
      deltaContent.ops = deltaContent.ops.map((op) => {
        if (op.attributes && op.attributes.link) {
          // Validate and sanitize link
          if (!validateSafeURL(op.attributes.link)) {
            // Remove invalid link
            delete op.attributes.link
          }
        }
        return op
      })
    }

    const plainText = getPlainText(deltaContent)
    
    onChange({
      richText: sanitizedContent,
      delta: deltaContent,
      plainText: plainText,
    })
  }

  // Sanitize initial value
  useEffect(() => {
    if (value && typeof value === 'string' && value !== value?.richText) {
      // If value is HTML string, sanitize it
      const sanitized = sanitizeRichText(value)
      if (sanitized !== value) {
        // Value was sanitized, update it
        onChange({
          richText: sanitized,
          delta: null,
          plainText: stripHTML(sanitized),
        })
      }
    }
  }, []) // Only run on mount

  return (
    <div className="rich-text-editor">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value?.richText || value || ''}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        className="bg-white rounded-lg"
        style={{
          minHeight: '200px',
        }}
      />
    </div>
  )
}

export default RichTextEditor

